import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { adminApi, setNoindex } from '../lib/adminApi';
import { Lock, Mail, ArrowRight } from 'lucide-react';

const AdminLoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotDone, setForgotDone] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const next = location.state?.from || '/admin';

  useEffect(() => {
    const undo = setNoindex();
    document.title = 'Admin — Dionlovelle';
    // If already authenticated, redirect
    adminApi.me().then(() => navigate(next, { replace: true })).catch(() => {});
    return undo;
  }, [navigate, next]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await adminApi.login(email.trim(), password);
      navigate(next, { replace: true });
    } catch (err) {
      setError(err.message || 'Unable to sign in.');
    } finally {
      setBusy(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await adminApi.forgotPassword(forgotEmail.trim());
      setForgotDone(true);
    } catch (err) {
      setError(err.message || 'Something went wrong.');
    }
  };

  return (
    <div className="min-h-screen bg-[color:var(--ink)] text-[color:var(--ivory)] flex items-center justify-center px-5 py-10"
         style={{ background: 'radial-gradient(120% 80% at 50% 0%, #16223A 0%, #0B0F1A 55%, #06070C 100%)' }}
         data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <p className="text-[10px] tracking-[0.42em] text-[color:var(--gold)] mb-3">✦ RESTRICTED ✦</p>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold mb-2">Admin Access</h1>
          <p className="text-sm text-[color:var(--ivory-muted)]/70">Dionlovelle CRM &amp; site management</p>
        </div>

        {!forgotOpen ? (
          <form
            onSubmit={handleSubmit}
            className="glass-card p-6 sm:p-8 space-y-4"
            data-testid="admin-login-form"
          >
            <label className="block">
              <span className="section-eyebrow block mb-2">Email</span>
              <div className="relative">
                <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 opacity-50" />
                <input
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@dionlovelle.com"
                  className="dl-input pl-10"
                  data-testid="admin-login-email"
                />
              </div>
            </label>
            <label className="block">
              <span className="section-eyebrow block mb-2">Password</span>
              <div className="relative">
                <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 opacity-50" />
                <input
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••••••"
                  className="dl-input pl-10"
                  data-testid="admin-login-password"
                />
              </div>
            </label>

            {error && (
              <p className="text-sm text-red-300 text-center" data-testid="admin-login-error">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={busy}
              className="glow-button w-full disabled:opacity-70"
              data-testid="admin-login-submit"
            >
              {busy ? 'SIGNING IN…' : (<>SIGN IN <ArrowRight size={16} strokeWidth={2.5} /></>)}
            </button>

            <div className="text-center pt-2">
              <button
                type="button"
                onClick={() => { setForgotOpen(true); setForgotEmail(email); setForgotDone(false); setError(''); }}
                className="text-xs text-[color:var(--ivory-muted)]/70 hover:text-[color:var(--gold)] transition-colors underline underline-offset-4"
                data-testid="admin-forgot-link"
              >
                Forgot your password?
              </button>
            </div>
          </form>
        ) : (
          <form
            onSubmit={handleForgot}
            className="glass-card p-6 sm:p-8 space-y-4"
            data-testid="admin-forgot-form"
          >
            <h2 className="font-display text-2xl">Password reset</h2>
            {forgotDone ? (
              <div>
                <p className="text-sm text-[color:var(--ivory-muted)]/85">
                  If the email matches an admin, a reset link has been generated. Ask the site operator to check the
                  backend logs for the link (valid for 1 hour), then use it to set a new password.
                </p>
                <button
                  type="button"
                  onClick={() => setForgotOpen(false)}
                  className="ghost-button mt-4 w-full"
                >
                  BACK TO SIGN IN
                </button>
              </div>
            ) : (
              <>
                <p className="text-sm text-[color:var(--ivory-muted)]/70">
                  Enter your admin email. A single-use reset link will be logged to the server console (valid 1 hour).
                </p>
                <input
                  type="email"
                  required
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="you@dionlovelle.com"
                  className="dl-input"
                  data-testid="admin-forgot-email"
                />
                {error && <p className="text-sm text-red-300 text-center">{error}</p>}
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setForgotOpen(false)}
                    className="ghost-button flex-1"
                  >
                    CANCEL
                  </button>
                  <button type="submit" className="glow-button flex-1" data-testid="admin-forgot-submit">
                    SEND RESET LINK
                  </button>
                </div>
              </>
            )}
          </form>
        )}

        <p className="text-center text-[11px] text-[color:var(--ivory-muted)]/40 mt-6">
          This area is not indexed by search engines. Public visitors will be redirected.
        </p>
      </div>
    </div>
  );
};

export default AdminLoginPage;
